import { Invoice, Client } from '../types';

export interface AutoBillingConfig {
  id: string;
  clientId: string;
  clientName: string;
  domain: string;
  monthlyAmount: number;
  billingDay: number; // 1-31
  isActive: boolean;
  lastBilledDate?: string;
  nextBillingDate: string;
  description: string;
  ivaPercentage: number;
  irpfPercentage: number;
  companyId: string;
}

export interface EmailConfig {
  smtpHost: string;
  smtpPort: number;
  smtpUser: string;
  smtpPassword: string;
  fromEmail: string;
  fromName: string;
}

export class AutoBillingService {
  private static readonly STORAGE_KEY = 'autoBillingConfigs';
  private static readonly EMAIL_CONFIG_KEY = 'emailConfig';

  // ===== CONFIGURACIÓN DE FACTURACIÓN AUTOMÁTICA =====
  
  static getAutoBillingConfigs(): AutoBillingConfig[] {
    try {
      const configs = localStorage.getItem(this.STORAGE_KEY);
      return configs ? JSON.parse(configs) : [];
    } catch (error) {
      console.error('Error al obtener configuraciones:', error);
      return [];
    }
  }

  static saveAutoBillingConfig(config: AutoBillingConfig): void {
    try {
      const configs = this.getAutoBillingConfigs();
      const existingIndex = configs.findIndex(c => c.id === config.id);
      
      if (existingIndex >= 0) {
        configs[existingIndex] = config;
      } else {
        configs.push(config);
      }
      
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(configs));
    } catch (error) {
      console.error('Error al guardar configuración:', error);
    }
  }

  static deleteAutoBillingConfig(id: string): void {
    try {
      const configs = this.getAutoBillingConfigs();
      const filtered = configs.filter(c => c.id !== id);
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(filtered));
    } catch (error) {
      console.error('Error al eliminar configuración:', error);
    }
  }

  // ===== CONFIGURACIÓN DE EMAIL =====
  
  static getEmailConfig(): EmailConfig | null {
    try {
      const config = localStorage.getItem(this.EMAIL_CONFIG_KEY);
      return config ? JSON.parse(config) : null;
    } catch (error) {
      console.error('Error al obtener configuración de email:', error);
      return null;
    }
  }

  static saveEmailConfig(config: EmailConfig): void {
    try {
      localStorage.setItem(this.EMAIL_CONFIG_KEY, JSON.stringify(config));
    } catch (error) {
      console.error('Error al guardar configuración de email:', error);
    }
  }

  // ===== GENERACIÓN AUTOMÁTICA DE FACTURAS =====
  
  static generateMonthlyInvoice(config: AutoBillingConfig, nextInvoiceNumber: number): Invoice {
    const today = new Date();
    const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    
    const invoice: Invoice = {
      id: `auto_${Date.now()}`,
      number: nextInvoiceNumber,
      date: today,
      dueDate: new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000), // 30 días
      clientId: config.clientId,
      companyId: config.companyId || '',
      client: { id: config.clientId, name: config.clientName } as Client,
      description: `Mantenimiento web - ${config.domain} - ${lastMonth.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}`,
      unitPrice: config.monthlyAmount,
      quantity: 1,
      subtotal: config.monthlyAmount,
      ivaPercentage: config.ivaPercentage,
      ivaAmount: Math.round(config.monthlyAmount * (config.ivaPercentage / 100) * 100) / 100,
      irpfPercentage: config.irpfPercentage,
      irpfAmount: Math.round(config.monthlyAmount * (config.irpfPercentage / 100) * 100) / 100,
      total: Math.round((config.monthlyAmount + 
        (config.monthlyAmount * (config.ivaPercentage / 100)) - 
        (config.monthlyAmount * (config.ivaPercentage / 100))) * 100) / 100,
      status: 'sent',
      createdAt: today,
      updatedAt: today
    };

    return invoice;
  }

  // ===== ENVÍO AUTOMÁTICO POR EMAIL =====
  
  static async sendInvoiceByEmail(invoice: Invoice, client: Client, emailConfig: EmailConfig): Promise<boolean> {
    try {
      // Crear el contenido del email
      const emailContent = this.createInvoiceEmail(invoice, client);
      
      // Enviar usando EmailJS (alternativa gratuita a SMTP directo)
      if (window.EmailJS) {
        const result = await window.EmailJS.send(
          'service_auto_billing', // ID del servicio de EmailJS
          'template_invoice', // ID de la plantilla
          {
            to_email: client.email,
            to_name: client.name,
            invoice_number: invoice.number,
            invoice_date: invoice.date.toLocaleDateString('es-ES'),
            invoice_total: invoice.total.toFixed(2) + '€',
            invoice_due_date: (invoice.dueDate || new Date(invoice.date.getTime() + 30 * 24 * 60 * 60 * 1000)).toLocaleDateString('es-ES'),
            company_name: 'Daniel Vázquez Facturación',
            company_email: emailConfig.fromEmail
          },
          'user_emailjs_key' // Tu clave pública de EmailJS
        );
        
        console.log('✅ Factura enviada por EmailJS:', result);
        return true;
      }
      
      // Fallback: mostrar el email para copiar/pegar
      this.showEmailFallback(emailContent, client.email || 'sin-email@cliente.com', invoice.number);
      return false;
      
    } catch (error) {
      console.error('❌ Error al enviar factura por email:', error);
      return false;
    }
  }

  private static createInvoiceEmail(invoice: Invoice, client: Client): string {
    const dueDate = invoice.dueDate || new Date(invoice.date.getTime() + 30 * 24 * 60 * 60 * 1000);
    
    return `
Asunto: Factura ${invoice.number} - Mantenimiento Web ${invoice.date.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}

Estimado/a ${client.name},

Adjunto encontrará la factura ${invoice.number} correspondiente al mantenimiento web del mes de ${invoice.date.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}.

DETALLES DE LA FACTURA:
- Número: ${invoice.number}
- Fecha: ${invoice.date.toLocaleDateString('es-ES')}
- Vencimiento: ${dueDate.toLocaleDateString('es-ES')}
- Importe total: ${invoice.total.toFixed(2)}€

CONCEPTO:
- ${invoice.description}: ${invoice.unitPrice.toFixed(2)}€

IMPORTES:
- Subtotal: ${invoice.subtotal.toFixed(2)}€
- IVA (${invoice.ivaPercentage}%): ${invoice.ivaAmount.toFixed(2)}€
- IRPF (${invoice.irpfPercentage}%): -${invoice.irpfAmount.toFixed(2)}€
- TOTAL: ${invoice.total.toFixed(2)}€

MÉTODOS DE PAGO:
- Transferencia bancaria
- PayPal
- Tarjeta de crédito

Para cualquier consulta, no dude en contactarnos.

Saludos cordiales,
Daniel Vázquez Facturación
${invoice.date.toLocaleDateString('es-ES')}
    `.trim();
  }

  private static showEmailFallback(emailContent: string, clientEmail: string, invoiceNumber: number): void {
    const modal = document.createElement('div');
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0,0,0,0.8);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10000;
    `;
    
    modal.innerHTML = `
      <div style="background: white; padding: 20px; border-radius: 8px; max-width: 600px; max-height: 80vh; overflow-y: auto;">
        <h3>📧 Email para enviar manualmente</h3>
        <p><strong>Para:</strong> ${clientEmail}</p>
        <p><strong>Asunto:</strong> Factura {invoiceNumber} - Mantenimiento Web</p>
        <textarea readonly style="width: 100%; height: 300px; margin: 10px 0; padding: 10px; font-family: monospace;">${emailContent}</textarea>
        <div style="text-align: center;">
          <button onclick="navigator.clipboard.writeText(document.querySelector('textarea').value)" style="background: #3b82f6; color: white; padding: 10px 20px; border: none; border-radius: 4px; cursor: pointer; margin: 5px;">📋 Copiar contenido</button>
          <button onclick="this.closest('.modal').remove()" style="background: #6b7280; color: white; padding: 10px 20px; border: none; border-radius: 4px; cursor: pointer; margin: 5px;">❌ Cerrar</button>
        </div>
      </div>
    `;
    
    modal.className = 'modal';
    document.body.appendChild(modal);
  }

  // ===== VERIFICACIÓN DIARIA DE FACTURACIÓN =====
  
  static checkDailyBilling(): AutoBillingConfig[] {
    const today = new Date();
    
    const configs = this.getAutoBillingConfigs();
    const dueConfigs = configs.filter(config => {
      if (!config.isActive) return false;
      
      const nextBilling = new Date(config.nextBillingDate);
      return nextBilling <= today;
    });
    
    return dueConfigs;
  }

  static updateNextBillingDate(configId: string): void {
    const configs = this.getAutoBillingConfigs();
    const config = configs.find(c => c.id === configId);
    
    if (config) {
      const today = new Date();
      let nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, config.billingDay);
      
      // Si el día no existe en el mes siguiente (ej: 31 en febrero), usar el último día del mes
      while (nextMonth.getMonth() !== (today.getMonth() + 1) % 12) {
        nextMonth.setDate(nextMonth.getDate() - 1);
      }
      
      config.nextBillingDate = nextMonth.toISOString();
      config.lastBilledDate = today.toISOString();
      
      this.saveAutoBillingConfig(config);
    }
  }
}

// Extender la interfaz Window para EmailJS
declare global {
  interface Window {
    EmailJS?: {
      send: (serviceId: string, templateId: string, templateParams: any, userId: string) => Promise<any>;
    };
  }
} 